'use client';

import { Pill, VisuallyHidden } from '@mantine/core';
import Image from 'next/image';
import type { ReactNode } from 'react';

import {
	ArrestorDescription,
	CommunicationLinesDescription,
	CrossArmDescription,
	PowerDistributionPrimaryLinesDescription,
	PrimaryFuseDescription,
	PrimaryInsulatorDescription,
	SecondaryLinesDescription,
	ServiceDropDescription,
	TrainerDescription,
	UtilityTransformerDescription,
} from '../../../app/(client)/(authenticated)/reportProblem/questions/(premise)/problemType/EquipmentDescriptions';
import {
	HelpContent,
	HelpContentHeading,
} from '../../../app/(client)/(authenticated)/reportProblem/questions/(premise)/problemType/HelpContent';
import { EquipmentOwner } from '../EquipmentOwner';
import type { OwnershipRow } from '../OwnershipRow';
import { ResponsibilityTable } from '../ResponsibilityTable';
import overheadEquipmentBase from './overheadEquipment.webp';
import styles from './OverheadEquipmentDetails.module.scss';
import overheadEquipmentLabels from './overheadEquipmentLabels.svg';

const overheadOwnershipData = [
	{ name: 'Arrestor', owner: EquipmentOwner.DTE, description: <ArrestorDescription /> },
	{ name: 'Primary fuse', owner: EquipmentOwner.DTE, description: <PrimaryFuseDescription /> },
	{ name: 'Cable trainer', owner: EquipmentOwner.DTE, description: <TrainerDescription /> },
	{ name: 'Cross arm', owner: EquipmentOwner.DTE, description: <CrossArmDescription /> },
	{
		name: 'Primary insulator',
		owner: EquipmentOwner.DTE,
		description: <PrimaryInsulatorDescription />,
	},
	{
		name: 'Primary lines',
		owner: EquipmentOwner.DTE,
		description: <PowerDistributionPrimaryLinesDescription />,
	},
	{
		name: 'Transformer',
		owner: EquipmentOwner.DTE,
		description: <UtilityTransformerDescription />,
	},
	{ name: 'Service drop', owner: EquipmentOwner.DTE, description: <ServiceDropDescription /> },
	{
		name: 'Secondary lines',
		owner: EquipmentOwner.DTE,
		description: <SecondaryLinesDescription />,
	},
	{
		name: 'Cable lines',
		owner: EquipmentOwner.OtherUtility,
		description: <CommunicationLinesDescription />,
	},
	{
		name: 'Communication lines',
		owner: EquipmentOwner.OtherUtility,
		description: <CommunicationLinesDescription />,
	},
] as const satisfies readonly OwnershipRow[];

export function OverheadEquipmentDetails(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Overhead equipment, Distribution pole and equipment</h4>
				<Pill.Group>
					<Pill>Overhead equipment</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<figure className={styles['equipmentFigure']}>
				<Image
					src={overheadEquipmentBase}
					alt="Picture of overhead equipment attached to a distribution pole"
					className={styles['baseImage']}
				/>
				<Image
					src={overheadEquipmentLabels}
					alt="Labels describing overhead equipment"
					className={styles['labelsImage']}
				/>
				<VisuallyHidden component="figcaption">
					Diagram of overhead equipment with components labeled.
				</VisuallyHidden>
			</figure>

			<ResponsibilityTable data={overheadOwnershipData} />
		</HelpContent>
	);
}
